import type {
  RequestStatus,
  RequestItemStatus,
  RequestItemPriority,
  ClientCategory,
} from '../api/types'
import {
  CUSTOMS_STATUS_LABELS_RU,
  ITEM_STATUS_LABELS_RU,
  PROCUREMENT_STATE_LABELS,
  REQUEST_STATUS_LABELS_RU,
} from '@crm/shared'
import type { CustomsCheckStatus, RequestItemProcurementState } from '@crm/shared'

type Color = 'gray' | 'blue' | 'orange' | 'yellow' | 'green' | 'teal' | 'red' | 'purple' | 'indigo'

const colorClasses: Record<Color, string> = {
  gray: 'bg-gray-100 text-gray-600',
  blue: 'bg-blue-100 text-blue-700',
  orange: 'bg-orange-100 text-orange-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  green: 'bg-green-100 text-green-700',
  teal: 'bg-teal-100 text-teal-700',
  red: 'bg-red-100 text-red-700',
  purple: 'bg-purple-100 text-purple-700',
  indigo: 'bg-indigo-100 text-indigo-700',
}

interface Props {
  children: React.ReactNode
  color?: Color
  className?: string
}

export function Badge({ children, color = 'gray', className = '' }: Props) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClasses[color]} ${className}`}
    >
      {children}
    </span>
  )
}

// ----- Request Status -----
// Подписи статусов — единый источник в @crm/shared (REQUEST_STATUS_LABELS_RU /
// ITEM_STATUS_LABELS_RU). Раньше здесь жили копии словарей, и одна из них
// (StatusEditor) разошлась с каноном.

const REQUEST_STATUS_COLORS: Record<RequestStatus, Color> = {
  DRAFT: 'gray',
  NEW: 'blue',
  SENT_TO_PROCUREMENT: 'purple',
  IN_PROGRESS: 'orange',
  PARTIALLY_PROCESSED: 'yellow',
  QUOTATION_READY: 'green',
  QUOTATION_CREATED: 'yellow',
  QUOTE_SENT: 'teal',
  CLOSED_WON: 'green',
  CLOSED_LOST: 'red',
  ARCHIVED: 'gray',
}

export function RequestStatusBadge({
  status,
  isPartiallyProcessed = false,
}: {
  status: RequestStatus
  isPartiallyProcessed?: boolean
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <Badge color={REQUEST_STATUS_COLORS[status]}>{REQUEST_STATUS_LABELS_RU[status]}</Badge>
      {isPartiallyProcessed && status !== 'QUOTATION_READY' && (
        <Badge color="yellow">Частично</Badge>
      )}
    </span>
  )
}

export function requestStatusLabel(status: RequestStatus): string {
  return REQUEST_STATUS_LABELS_RU[status]
}

// ----- Item Status -----

const ITEM_STATUS_COLORS: Record<RequestItemStatus, Color> = {
  NEW: 'blue',
  IN_QUEUE: 'blue',
  IN_PROGRESS: 'orange',
  NEEDS_INFO: 'red',
  QUOTED: 'green',
  PARTIAL: 'yellow',
  READY_FOR_QUOTATION: 'teal',
  QUOTATION_CREATED: 'yellow',
  ORDERED: 'green',
  ORDER_CONFIRMED: 'green',
  REJECTED_BY_CUSTOMER: 'red',
  REWORK: 'red',
  NOT_FOUND: 'gray',
  NOT_ACTUAL: 'gray',
  CANCELLED: 'gray',
  NO_RESPONSE: 'gray',
  // legacy — серые
  WORK_PROCUREMENT: 'gray',
  WORK_SALES: 'gray',
  KP_SENT: 'gray',
  TO_PROCUREMENT: 'gray',
  INVOICE: 'gray',
  ARCHIVED: 'gray',
  NO_OFFER: 'gray',
  REFUSED: 'gray',
}

export function ItemStatusBadge({ status }: { status: RequestItemStatus }) {
  return <Badge color={ITEM_STATUS_COLORS[status]}>{ITEM_STATUS_LABELS_RU[status]}</Badge>
}

export function itemStatusLabel(status: RequestItemStatus) {
  return ITEM_STATUS_LABELS_RU[status]
}

// ----- Priority -----

const PRIORITY_LABELS: Record<RequestItemPriority, string> = {
  HIGH: 'Высокий',
  MEDIUM: 'Средний',
  LOW: 'Низкий',
}

const PRIORITY_COLORS: Record<RequestItemPriority, Color> = {
  HIGH: 'red',
  MEDIUM: 'orange',
  LOW: 'gray',
}

export function PriorityBadge({ priority }: { priority: RequestItemPriority }) {
  return <Badge color={PRIORITY_COLORS[priority]}>{PRIORITY_LABELS[priority]}</Badge>
}

// ----- Category -----

const CATEGORY_COLORS: Record<ClientCategory, Color> = {
  A: 'red',
  B: 'orange',
  C: 'gray',
}

export function CategoryBadge({ category }: { category: ClientCategory }) {
  return <Badge color={CATEGORY_COLORS[category]}>{category}</Badge>
}

// ----- Procurement request status (агрегат по заявке, per-procurement) -----

export type ProcRequestStatus = 'NEW' | 'PARTIAL' | 'IN_PROGRESS' | 'READY' | 'REJECTED'

const PROC_REQ_STATUS_LABELS: Record<ProcRequestStatus, string> = {
  NEW: 'Новая',
  PARTIAL: 'В работе',
  IN_PROGRESS: 'В работе',
  READY: 'Проквотирована',
  REJECTED: 'Отказ',
}

const PROC_REQ_STATUS_COLORS: Record<ProcRequestStatus, Color> = {
  NEW: 'blue',
  PARTIAL: 'orange',
  IN_PROGRESS: 'orange',
  READY: 'green',
  REJECTED: 'red',
}

/** Закупщицкий статус заявки. Для PROCUREMENT — personal, для ROS — агрегат команды. */
export function ProcRequestStatusBadge({ status }: { status: ProcRequestStatus }) {
  return <Badge color={PROC_REQ_STATUS_COLORS[status]}>{PROC_REQ_STATUS_LABELS[status]}</Badge>
}

// ----- Procurement state (per-user или агрегат команды для ROS) -----

const PROC_STATE_COLORS: Record<RequestItemProcurementState, Color> = {
  NEW: 'blue',
  REQUESTED: 'purple',
  WAITING: 'yellow',
  NEEDS_INFO: 'orange',
  QUOTED: 'green',
  NOT_FOUND: 'gray',
  NO_RESPONSE: 'gray',
  REFUSED: 'red',
}

/** Бейдж закупщицкого per-user статуса. Для PROCUREMENT — личный, для ROS — агрегат команды. */
export function ProcItemStatusBadge({
  state,
  team = false,
}: {
  state: RequestItemProcurementState
  /** true — добавить подпись «· команда» (для ROS). */
  team?: boolean
}) {
  const label = PROCUREMENT_STATE_LABELS[state] ?? state
  return (
    <Badge color={PROC_STATE_COLORS[state] ?? 'gray'}>
      {label}
      {team && <span className="ml-1 text-[10px] opacity-70">· команда</span>}
    </Badge>
  )
}

// ----- Нетарифка (логист, Л1–Л3) -----

const CUSTOMS_STATUS_COLORS: Record<CustomsCheckStatus, Color> = {
  PENDING: 'gray',
  SENT: 'blue',
  ANSWERED: 'green',
  NEEDS_INFO: 'orange',
  NOT_REQUIRED: 'teal',
}

/** Статус проверки позиции логистом: очередь, у брокера, ответ, нужна информация, не требуется. */
export function CustomsStatusBadge({
  status,
  label,
}: {
  status: CustomsCheckStatus
  /** Своя подпись: у менеджера «В очереди» — «Отправлено логисту». */
  label?: string
}) {
  return (
    <Badge color={CUSTOMS_STATUS_COLORS[status] ?? 'gray'}>
      {label ?? CUSTOMS_STATUS_LABELS_RU[status] ?? status}
    </Badge>
  )
}
